import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { marketplaceAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';

function AutoLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { autoLogin } = useAuth();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Validating credentials...');

  useEffect(function() {
    const id = searchParams.get('id');
    const role = searchParams.get('role');

    if (!id || !role) {
      setError('Missing required parameters. Please use the link from AALIGN platform.');
      return;
    }

    // Validate role
    if (role !== 'seller' && role !== 'buyer') {
      setError('Invalid role specified. Role must be "seller" or "buyer".');
      return;
    }

    setStatus('Authenticating with AALIGN...');

    // Call auto-login API
    marketplaceAPI.autoLogin(id, role)
      .then(function(response) {
        if (response.data.success) {
          setStatus('Login successful! Redirecting...');
          // Use the autoLogin method from AuthContext
          autoLogin(response.data);
          
          // Redirect based on role
          setTimeout(function() {
            if (role === 'seller') {
              navigate('/marketplace');
            } else {
              navigate('/marketplace/sellers');
            }
          }, 500);
        } else {
          setError(response.data.error || 'Authentication failed');
        }
      })
      .catch(function(err) {
        console.error('Auto-login error:', err);
        const errorMsg = err.response?.data?.detail || err.message || 'Failed to authenticate';
        setError(errorMsg);
      });
  }, [searchParams, navigate, autoLogin]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-white">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Authentication Failed</h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <p className="text-sm text-slate-500">
              Please return to the AALIGN platform and try again, or contact support if the issue persists.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-white">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mb-6">
            <img 
              src="/aalign-logo.png" 
              alt="AALIGN" 
              className="h-12 mx-auto"
              onError={function(e) { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="flex items-center justify-center mb-4">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Signing you in...</h1>
          <p className="text-slate-600">{status}</p>
        </div>
      </div>
    </div>
  );
}

export default AutoLoginPage;
